/**
 * Cross-Sell Analytics Page
 *
 * Two tabs:
 * 1. Cross-Sell — transition matrix between product lanes (existing)
 * 2. Acquisition Expansion — which acquisition channel drives lane expansion,
 *    with unit economics (COGS, margin, CAC, ROAS).
 */

import { useState, useEffect, useCallback } from 'react'
import { format, subDays } from 'date-fns'
import { crossSellAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { DateRangePicker } from '../components/DateRangePicker'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button
} from '../components/ui'
import { RefreshCw, Loader2, AlertCircle, Users, ArrowRightLeft, TrendingUp, DollarSign, Clock, Target, BarChart3 } from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  ComposedChart, Line, Legend,
} from 'recharts'
import { channelColor, channelLabel } from '../lib/channels'
import AttributionSettings, { DEFAULT_ENGINE } from '../components/AttributionSettings'
import { DEFAULT_LOOKBACK } from '../lib/attributionLookback'

function getDefaultDateRange() {
  const end = new Date()
  const start = subDays(end, 179)
  return { from: start, to: end }
}

function formatNumber(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US').format(value)
}

function formatCurrency(dollars) {
  if (dollars === null || dollars === undefined || isNaN(dollars)) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(dollars)
}

function formatPct(value) {
  if (value === null || value === undefined || isNaN(value)) return '-'
  return `${value}%`
}

const LANE_COLORS = {
  glp1: '#10B981',
  phentermine: '#3B82F6',
  trt: '#8B5CF6',
  hair: '#F59E0B',
  skin: '#EC4899',
  ed: '#EF4444',
  nad: '#14B8A6',
}

// Channel colours come from lib/channels, which resolves both the Capitalized
// BigQuery labels this page used to key on and the canonical ids the API now
// sends. Keeping its own map is what made Meta a different blue here than on
// every other report.

// ─── Tab definitions ──────────────────────────────────────

const TABS = [
  { id: 'crosssell', label: 'Cross-Sell' },
  { id: 'expansion', label: 'Acquisition Expansion' },
]

// ─── Cross-Sell Tab (original) ────────────────────────────

function CrossSellTab({ dateRange, onDateRangeChange }) {
  const { currentClinicId } = useAuthStore()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const params = {
        start: format(dateRange.from, 'yyyy-MM-dd'),
        end: format(dateRange.to, 'yyyy-MM-dd'),
      }
      const response = await crossSellAPI.getReport(params)
      if (response.data.success) {
        setData(response.data.data)
      }
    } catch (err) {
      console.error('[CROSS-SELL] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [dateRange, currentClinicId])

  useEffect(() => { fetchData() }, [fetchData])

  const summary = data?.summary
  const matrix = data?.matrix || []
  const cohorts = data?.cohorts || []
  const allLanes = cohorts.map(c => ({ id: c.lane, name: c.display_name, color: c.color }))

  const heatmapLookup = {}
  for (const row of matrix) {
    if (!heatmapLookup[row.from_lane]) heatmapLookup[row.from_lane] = {}
    heatmapLookup[row.from_lane][row.to_lane] = row
  }

  const windowTotals = [
    { window: '0-7 days', count: matrix.reduce((s, r) => s + r.window_0_7d, 0), color: '#10B981' },
    { window: '8-30 days', count: matrix.reduce((s, r) => s + r.window_8_30d, 0), color: '#3B82F6' },
    { window: '31-60 days', count: matrix.reduce((s, r) => s + r.window_31_60d, 0), color: '#8B5CF6' },
    { window: '61-90 days', count: matrix.reduce((s, r) => s + r.window_61_90d, 0), color: '#F59E0B' },
  ]

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardContent className="py-4 px-4 flex items-center gap-3">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={onDateRangeChange} />
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1.5">Refresh</span>
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Card>
          <CardContent className="py-6">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
              <p className="text-destructive">{error}</p>
              <Button variant="outline" onClick={fetchData} className="mt-3">Try Again</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
              <p>Analyzing cross-sell patterns...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {!loading && !error && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Total Customers</span>
              </div>
              <p className="text-2xl font-bold">{formatNumber(summary.total_customers)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <ArrowRightLeft className="h-4 w-4 text-blue-500" />
                <span className="text-xs text-muted-foreground">Cross-Sell Customers</span>
              </div>
              <p className="text-2xl font-bold">{formatNumber(summary.crosssell_customers)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-green-500" />
                <span className="text-xs text-muted-foreground">Cross-Sell Rate</span>
              </div>
              <p className="text-2xl font-bold">{formatPct(summary.overall_crosssell_rate)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4 text-amber-500" />
                <span className="text-xs text-muted-foreground">Avg Days to Cross-Sell</span>
              </div>
              <p className="text-2xl font-bold">{summary.avg_days_to_crosssell}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="h-4 w-4 text-green-500" />
                <span className="text-xs text-muted-foreground">Cross-Sell Revenue</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(summary.total_crosssell_revenue)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Transition Matrix Heatmap */}
      {!loading && !error && allLanes.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Transition Matrix</CardTitle>
            <CardDescription>
              Rows = origin lane, columns = destination lane. Cells show customer count and transition rate.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium whitespace-nowrap">From \ To</th>
                    {allLanes.map(lane => (
                      <th key={lane.id} className="text-center p-3 font-medium whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1.5">
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: lane.color }}
                          />
                          {lane.name}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allLanes.map(fromLane => (
                    <tr key={fromLane.id} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-medium whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: fromLane.color }}
                          />
                          {fromLane.name}
                        </div>
                      </td>
                      {allLanes.map(toLane => {
                        const cell = heatmapLookup[fromLane.id]?.[toLane.id]
                        if (fromLane.id === toLane.id) {
                          return (
                            <td key={toLane.id} className="p-3 text-center bg-muted/20">
                              <span className="text-muted-foreground">-</span>
                            </td>
                          )
                        }
                        if (!cell) {
                          return (
                            <td key={toLane.id} className="p-3 text-center">
                              <span className="text-muted-foreground">0</span>
                            </td>
                          )
                        }
                        const intensity = Math.min(cell.transition_rate / 15, 1)
                        const bg = `rgba(16, 185, 129, ${intensity * 0.3})`
                        return (
                          <td key={toLane.id} className="p-3 text-center" style={{ backgroundColor: bg }}>
                            <div className="font-mono font-medium">{cell.customers}</div>
                            <div className="text-xs text-muted-foreground">{formatPct(cell.transition_rate)}</div>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Transitions Detail */}
      {!loading && !error && matrix.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Top Transitions</CardTitle>
            <CardDescription>
              Detailed transition metrics sorted by customer count
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium whitespace-nowrap">Transition</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap">Customers</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden sm:table-cell">Rate</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden md:table-cell">P50 Days</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden md:table-cell">P75 Days</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden lg:table-cell">P90 Days</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden sm:table-cell">Revenue</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden lg:table-cell">Avg Order</th>
                  </tr>
                </thead>
                <tbody>
                  {matrix.map((row, i) => (
                    <tr key={i} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-medium whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="inline-block w-2 h-2 rounded-full"
                            style={{ backgroundColor: LANE_COLORS[row.from_lane] || '#6B7280' }}
                          />
                          <span>{row.from_display_name}</span>
                          <span className="text-muted-foreground mx-1">-&gt;</span>
                          <span
                            className="inline-block w-2 h-2 rounded-full"
                            style={{ backgroundColor: LANE_COLORS[row.to_lane] || '#6B7280' }}
                          />
                          <span>{row.to_display_name}</span>
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono">{formatNumber(row.customers)}</td>
                      <td className="p-3 text-right font-mono hidden sm:table-cell">{formatPct(row.transition_rate)}</td>
                      <td className="p-3 text-right font-mono hidden md:table-cell">{row.p50_days}d</td>
                      <td className="p-3 text-right font-mono hidden md:table-cell">{row.p75_days}d</td>
                      <td className="p-3 text-right font-mono hidden lg:table-cell">{row.p90_days}d</td>
                      <td className="p-3 text-right font-mono hidden sm:table-cell">{formatCurrency(row.crosssell_revenue)}</td>
                      <td className="p-3 text-right font-mono hidden lg:table-cell">{formatCurrency(row.avg_order_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cohort Table */}
      {!loading && !error && cohorts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Lane Cohorts</CardTitle>
            <CardDescription>
              Per-lane cohort sizes and cross-sell rates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium whitespace-nowrap">Lane</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap">Cohort Size</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap">Cross-Sell Count</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap">Cross-Sell Rate</th>
                    <th className="text-left p-3 font-medium whitespace-nowrap hidden sm:table-cell">Top Target</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden md:table-cell">Cross-Sell Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.map(cohort => (
                    <tr key={cohort.lane} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-medium whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: cohort.color }}
                          />
                          {cohort.display_name}
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono">{formatNumber(cohort.cohort_size)}</td>
                      <td className="p-3 text-right font-mono">{formatNumber(cohort.crosssell_count)}</td>
                      <td className="p-3 text-right font-mono">
                        <span className={cohort.crosssell_rate >= 10 ? 'text-green-600' : ''}>
                          {formatPct(cohort.crosssell_rate)}
                        </span>
                      </td>
                      <td className="p-3 whitespace-nowrap hidden sm:table-cell">
                        {cohort.top_target_display_name ? (
                          <div className="flex items-center gap-1.5">
                            <span
                              className="inline-block w-2 h-2 rounded-full"
                              style={{ backgroundColor: LANE_COLORS[cohort.top_target_lane] || '#6B7280' }}
                            />
                            {cohort.top_target_display_name}
                          </div>
                        ) : <span className="text-muted-foreground">-</span>}
                      </td>
                      <td className="p-3 text-right font-mono hidden md:table-cell">{formatCurrency(cohort.crosssell_revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Time Window Distribution */}
      {!loading && !error && windowTotals.some(w => w.count > 0) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Time to Cross-Sell</CardTitle>
            <CardDescription>
              Distribution of days between first purchase in origin lane and first purchase in destination lane
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={windowTotals} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="window" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      color: 'hsl(var(--card-foreground))'
                    }}
                    formatter={(value) => [formatNumber(value), 'Customers']}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {windowTotals.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!loading && !error && (!data?.matrix || data.matrix.length === 0) && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <ArrowRightLeft className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No cross-sell data found</p>
              <p className="text-sm mt-1">Try expanding the date range (default is 180 days) or check that this clinic has multi-lane customers.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ─── Acquisition Expansion Tab ────────────────────────────

function ExpansionTab({ dateRange, onDateRangeChange }) {
  const { currentClinicId } = useAuthStore()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [engine, setEngine] = useState(DEFAULT_ENGINE)
  const lookbackDays = DEFAULT_LOOKBACK // all time for every brand; not a choice on the page

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const params = {
        start: format(dateRange.from, 'yyyy-MM-dd'),
        end: format(dateRange.to, 'yyyy-MM-dd'),
        engine,
        lookbackDays,
      }
      const response = await crossSellAPI.getExpansion(params)
      if (response.data.success) {
        setData(response.data.data)
      }
    } catch (err) {
      console.error('[EXPANSION] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [dateRange, engine, lookbackDays, currentClinicId])

  useEffect(() => { fetchData() }, [fetchData])

  const summary = data?.summary
  const channelCohorts = data?.channel_cohorts || []
  const expansionEcon = data?.expansion_economics || []

  // Build channel × lane table data
  const channels = [...new Set(channelCohorts.map(c => c.channel))]
  const lanes = [...new Set(channelCohorts.map(c => c.acquisition_lane))]
  const cohortLookup = {}
  for (const c of channelCohorts) {
    cohortLookup[`${c.channel}|${c.acquisition_lane}`] = c
  }

  // Chart data: revenue vs COGS per channel
  const channelAgg = {}
  for (const c of channelCohorts) {
    if (!channelAgg[c.channel]) {
      channelAgg[c.channel] = { channel: c.channel, revenue: 0, cogs: 0, margin: 0 }
    }
    channelAgg[c.channel].revenue += c.total_revenue
    channelAgg[c.channel].cogs += c.cogs
    channelAgg[c.channel].margin += c.margin
  }
  const chartData = Object.values(channelAgg)
    .map(d => ({
      ...d,
      margin_pct: d.revenue > 0 ? Math.round(d.margin / d.revenue * 1000) / 10 : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue)

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardContent className="py-4 px-4 flex flex-wrap items-end gap-3">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={onDateRangeChange} />
          <AttributionSettings models={data?.meta?.attribution?.models} engine={engine} onEngineChange={setEngine} />
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1.5">Refresh</span>
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Card>
          <CardContent className="py-6">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
              <p className="text-destructive">{error}</p>
              <Button variant="outline" onClick={fetchData} className="mt-3">Try Again</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
              <p>Analyzing acquisition expansion patterns...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {!loading && !error && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Acquired</span>
              </div>
              <p className="text-2xl font-bold">{formatNumber(summary.total_acquired)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <ArrowRightLeft className="h-4 w-4 text-blue-500" />
                <span className="text-xs text-muted-foreground">Expanded</span>
              </div>
              <p className="text-2xl font-bold">{formatNumber(summary.total_expanded)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-green-500" />
                <span className="text-xs text-muted-foreground">Expansion Rate</span>
              </div>
              <p className="text-2xl font-bold">{formatPct(summary.overall_expansion_rate)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <Target className="h-4 w-4 text-amber-500" />
                <span className="text-xs text-muted-foreground">Blended CAC</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(summary.blended_cac)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="h-4 w-4 text-green-500" />
                <span className="text-xs text-muted-foreground">Blended ROAS</span>
              </div>
              <p className="text-2xl font-bold">{summary.blended_roas > 0 ? `${summary.blended_roas}x` : '-'}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Channel x Lane Table */}
      {!loading && !error && channelCohorts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Channel x Lane Cohorts</CardTitle>
            <CardDescription>
              Acquisition channel by starting lane — customers, expansion rate, CAC, and ROAS
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium whitespace-nowrap">Channel</th>
                    <th className="text-left p-3 font-medium whitespace-nowrap">Acq. Lane</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap">Customers</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap">Expanded</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap">Exp. Rate</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden md:table-cell">Avg Days</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden sm:table-cell">Revenue</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden md:table-cell">Margin</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden lg:table-cell">Ad Spend</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden sm:table-cell">CAC</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden sm:table-cell">ROAS</th>
                  </tr>
                </thead>
                <tbody>
                  {channelCohorts.map((c, i) => {
                    const expRateColor = c.expansion_rate >= 15
                      ? 'text-green-600 font-semibold'
                      : c.expansion_rate >= 8 ? 'text-green-600' : ''
                    return (
                      <tr key={i} className="border-t hover:bg-muted/30">
                        <td className="p-3 font-medium whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="inline-block w-2 h-2 rounded-full"
                              style={{ backgroundColor: channelColor(c.channel) }}
                            />
                            {channelLabel(c.channel)}
                          </div>
                        </td>
                        <td className="p-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="inline-block w-2 h-2 rounded-full"
                              style={{ backgroundColor: LANE_COLORS[c.acquisition_lane] || '#6B7280' }}
                            />
                            {c.acquisition_lane_display}
                          </div>
                        </td>
                        <td className="p-3 text-right font-mono">{formatNumber(c.customers)}</td>
                        <td className="p-3 text-right font-mono">{formatNumber(c.expanded_customers)}</td>
                        <td className={`p-3 text-right font-mono ${expRateColor}`}>{formatPct(c.expansion_rate)}</td>
                        <td className="p-3 text-right font-mono hidden md:table-cell">{c.avg_days_to_expand || '-'}</td>
                        <td className="p-3 text-right font-mono hidden sm:table-cell">{formatCurrency(c.total_revenue)}</td>
                        <td className="p-3 text-right font-mono hidden md:table-cell">
                          {formatCurrency(c.margin)}
                          {c.margin_pct > 0 && <span className="text-xs text-muted-foreground ml-1">({c.margin_pct}%)</span>}
                        </td>
                        <td className="p-3 text-right font-mono hidden lg:table-cell">{formatCurrency(c.ad_spend)}</td>
                        <td className="p-3 text-right font-mono hidden sm:table-cell">{c.cac > 0 ? formatCurrency(c.cac) : '-'}</td>
                        <td className="p-3 text-right font-mono hidden sm:table-cell">{c.roas > 0 ? `${c.roas}x` : '-'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expansion Economics Table */}
      {!loading && !error && expansionEcon.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Expansion Economics</CardTitle>
            <CardDescription>
              Lane-to-lane expansion transitions with revenue, COGS, and margin
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium whitespace-nowrap">From Lane</th>
                    <th className="text-left p-3 font-medium whitespace-nowrap">To Lane</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap">Customers</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap">Revenue</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden sm:table-cell">COGS</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap">Margin</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden md:table-cell">Margin %</th>
                    <th className="text-right p-3 font-medium whitespace-nowrap hidden md:table-cell">Avg Order</th>
                  </tr>
                </thead>
                <tbody>
                  {expansionEcon.map((row, i) => (
                    <tr key={i} className="border-t hover:bg-muted/30">
                      <td className="p-3 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="inline-block w-2 h-2 rounded-full"
                            style={{ backgroundColor: LANE_COLORS[row.from_lane] || '#6B7280' }}
                          />
                          {row.from_display_name}
                        </div>
                      </td>
                      <td className="p-3 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="inline-block w-2 h-2 rounded-full"
                            style={{ backgroundColor: LANE_COLORS[row.to_lane] || '#6B7280' }}
                          />
                          {row.to_display_name}
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono">{formatNumber(row.customers)}</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(row.revenue)}</td>
                      <td className="p-3 text-right font-mono hidden sm:table-cell">{formatCurrency(row.cogs)}</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(row.margin)}</td>
                      <td className="p-3 text-right font-mono hidden md:table-cell">{formatPct(row.margin_pct)}</td>
                      <td className="p-3 text-right font-mono hidden md:table-cell">{formatCurrency(row.avg_order_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Margin Chart: Revenue vs COGS per channel */}
      {!loading && !error && chartData.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Revenue vs COGS by Channel</CardTitle>
            <CardDescription>
              Stacked revenue and COGS with margin % overlay per acquisition channel
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="channel" className="text-xs" tickFormatter={channelLabel} />
                  <YAxis yAxisId="left" className="text-xs" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <YAxis yAxisId="right" orientation="right" className="text-xs" tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      color: 'hsl(var(--card-foreground))'
                    }}
                    labelFormatter={channelLabel}
                    formatter={(value, name) => {
                      if (name === 'margin_pct') return [`${value}%`, 'Margin %']
                      return [formatCurrency(value), name === 'revenue' ? 'Revenue' : 'COGS']
                    }}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="cogs" name="COGS" fill="#EF4444" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="margin_pct" name="Margin %" stroke="#F59E0B" strokeWidth={2} dot={{ r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!loading && !error && channelCohorts.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No expansion data found</p>
              <p className="text-sm mt-1">Expansion tracking requires BigQuery channel attribution data. Try a wider date range or check BigQuery configuration.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ─── Main Page Component ──────────────────────────────────

export default function CrossSellPage() {
  const [activeTab, setActiveTab] = useState('crosssell')
  const [dateRange, setDateRange] = useState(getDefaultDateRange)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Cross-Sell</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Customer transitions between product lanes — timing, rates, revenue, and acquisition economics
          </p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex border-b border-border">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'crosssell' && (
        <CrossSellTab dateRange={dateRange} onDateRangeChange={setDateRange} />
      )}
      {activeTab === 'expansion' && (
        <ExpansionTab dateRange={dateRange} onDateRangeChange={setDateRange} />
      )}
    </div>
  )
}
